import { apiClient } from '@/services/api-client';
import { useAuthStore } from '@/stores';

// Check if push is supported by the browser
export function isPushSupported(): boolean {
  return !!(window as any).OneSignal || ('serviceWorker' in navigator && 'PushManager' in window);
}

// Register user for OneSignal Push notifications
export async function subscribeToPushNotifications() {
  if (!isPushSupported()) {
    console.warn('Push notifications are not supported in this browser.');
    return null;
  }

  try {
    const OneSignal = (window as any).OneSignal;
    if (!OneSignal) {
      throw new Error('OneSignal SDK is not loaded on the window object.');
    }

    // 1. Resolve subscription object
    const sub = OneSignal.User?.pushSubscription || OneSignal.User?.PushSubscription;
    if (!sub) {
      throw new Error('OneSignal pushSubscription object not found.');
    }

    // 2. Opt-in to subscription and request permissions
    console.log('[Push] Opting in and requesting permission from OneSignal...');
    const optInPromise = sub.optIn();
    const requestPromise = OneSignal.Notifications?.requestPermission
      ? OneSignal.Notifications.requestPermission()
      : Promise.resolve();

    // 3. Login active user to OneSignal for user-specific targeting
    let loginPromise = Promise.resolve();
    const { user } = useAuthStore.getState();
    if (user?.username) {
      console.log('[Push] Syncing logged-in username with OneSignal:', user.username);
      loginPromise = OneSignal.login(user.username);
    }

    await Promise.all([optInPromise, requestPromise, loginPromise]);

    // 4. Poll for the Subscription ID assigned by OneSignal servers
    let subId = sub.id;
    let attempts = 0;
    while (!subId && attempts < 12) {
      await new Promise((r) => setTimeout(r, 500));
      subId = sub.id;
      attempts++;
      console.log(`[Push] Polling for OneSignal subId (attempt ${attempts}):`, subId);
    }

    if (!subId) {
      throw new Error('Failed to resolve OneSignal subscription ID.');
    }

    // 5. Register OneSignal device ID on our backend
    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    await apiClient.post('/notifications/register-device', {
      subscriptionId: subId,
      device_type: isMobile ? 'mobile' : 'desktop',
      platform: typeof navigator !== 'undefined' ? navigator.platform : undefined,
      browser: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    });
    console.log('Successfully registered OneSignal subscription on backend:', subId);
    return subId;
  } catch (err) {
    console.error('Failed to subscribe to OneSignal Push:', err);
    throw err;
  }
}

// Unsubscribe user from OneSignal Push notifications
export async function unsubscribeFromPushNotifications() {
  if (!isPushSupported()) return;

  try {
    const OneSignal = (window as any).OneSignal;
    if (OneSignal) {
      const sub = OneSignal.User?.pushSubscription || OneSignal.User?.PushSubscription;
      if (sub) {
        const subId = sub.id;
        
        // 1. Opt out from OneSignal
        await sub.optOut();
        
        // 2. Notify backend to delete subscription if we have a subscription ID
        if (subId) {
          await apiClient.post('/notifications/unsubscribe', { subscriptionId: subId });
        }
        console.log('Successfully opted out and unsubscribed from OneSignal Push.');
      }
    }
  } catch (err) {
    console.error('Failed to unsubscribe from OneSignal Push:', err);
    throw err;
  }
}

// Ensure OneSignal device is registered
export async function ensureOneSignalDeviceRegistered(...args: any[]) {
  if (!isPushSupported()) return null;
  try {
    return await subscribeToPushNotifications();
  } catch (err) {
    console.warn('[Push] ensureOneSignalDeviceRegistered warning:', err);
    return null;
  }
}

